/**
 * `@quadratic/editor/commands`: the editor command table without TipTap — ids, titles, groups and display keys. The
 * shell registers these with its command registry (shortcut help's Editor group, the palette's `editor.*` entries,
 * registry issues.editor.* "shortcut help / palette" ACs) from the app's main chunk, which must not load the editor
 * core; `extensions/core/commands.ts` adds `run`/`isActive` to the same rows, so the two can't drift.
 */

/** The heading levels the shared schema allows (StarterKit `heading.levels` in schema/extensions.ts). */
export const SCHEMA_HEADING_LEVELS: readonly number[] = [1, 2, 3];

export type CommandGroup = 'format' | 'block' | 'list' | 'insert';

export interface EditorCommandInfo {
  /** Palette / shortcut-help id (`palette.result[data-id=editor.bold]`). */
  readonly id: string;
  readonly title: string;
  readonly group: CommandGroup;
  /** Display keys in the shell's notation (`mod+b`); the bindings themselves are TipTap keymaps. */
  readonly keys?: readonly string[];
  /** Needs the host's UI (link popover, emoji picker, slash menu). */
  readonly ui?: 'link' | 'emoji-picker' | 'slash-menu';
  /** Only offered with a non-empty selection (palette, AC: editor.link with a selection). */
  readonly needsSelection?: boolean;
  /** The heading level this command needs the schema to allow. */
  readonly headingLevel?: number;
}

const heading = (level: 1 | 2 | 3 | 4): EditorCommandInfo => ({
  id: `editor.heading-${level}`,
  title: `Heading ${level}`,
  group: 'block',
  keys: [`mod+alt+${level}`],
  headingLevel: level,
});

export const EDITOR_COMMAND_INFO: readonly EditorCommandInfo[] = [
  { id: 'editor.bold', title: 'Bold', group: 'format', keys: ['mod+b'] },
  { id: 'editor.italic', title: 'Italic', group: 'format', keys: ['mod+i', 'mod+>'] },
  { id: 'editor.strike', title: 'Strikethrough', group: 'format', keys: ['mod+shift+x'] },
  { id: 'editor.code', title: 'Inline code', group: 'format', keys: ['mod+e'] },
  { id: 'editor.link', title: 'Add link', group: 'format', keys: ['mod+k'], ui: 'link', needsSelection: true },
  heading(1),
  heading(2),
  heading(3),
  heading(4),
  { id: 'editor.body-text', title: 'Body text', group: 'block', keys: ['mod+alt+0'] },
  { id: 'editor.bulleted-list', title: 'Bulleted list', group: 'list', keys: ['mod+shift+8'] },
  { id: 'editor.numbered-list', title: 'Numbered list', group: 'list', keys: ['mod+shift+9'] },
  { id: 'editor.checklist', title: 'Checklist', group: 'list', keys: ['mod+shift+7'] },
  { id: 'editor.toggle-checklist-item', title: 'Toggle checklist item', group: 'list', keys: ['mod+enter', 'alt+enter'] },
  {
    id: 'editor.toggle-checklist-nested',
    title: 'Toggle checklist item and nested items',
    group: 'list',
    keys: ['mod+shift+enter', 'alt+shift+enter'],
  },
  { id: 'editor.indent', title: 'Indent list item', group: 'list', keys: ['tab'] },
  { id: 'editor.outdent', title: 'Outdent list item', group: 'list', keys: ['shift+tab'] },
  { id: 'editor.quote', title: 'Quote', group: 'block' },
  { id: 'editor.code-block', title: 'Code block', group: 'block', keys: ['mod+shift+\\'] },
  { id: 'editor.divider', title: 'Divider', group: 'insert' },
  { id: 'editor.line-break', title: 'Line break', group: 'insert', keys: ['shift+enter'] },
  { id: 'editor.emoji-picker', title: 'Insert emoji…', group: 'insert', ui: 'emoji-picker' },
  { id: 'editor.slash-menu', title: 'Insert block…', group: 'insert', keys: ['/'], ui: 'slash-menu' },
];

/** The rows the schema supports (H4 only once the schema allows it). */
export function availableCommandInfo(headingLevels: readonly number[] = SCHEMA_HEADING_LEVELS): EditorCommandInfo[] {
  return EDITOR_COMMAND_INFO.filter((c) => c.headingLevel === undefined || headingLevels.includes(c.headingLevel));
}
